import React, { memo, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button } from '@mui/material';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import { TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { closeDialogAction, showMessageAction } from 'store/global/actions';
import {
  getLoanBrokerConditionByApplicationIdAction,
  getLoanConditionByLoanIdAction,
  getLoanApplicationTasksAction,
  rejectLoanConditionAction,
} from 'store/loanApplications/actions';

import { REJECTION_REASON_MAX_LENGTH } from './RejectConditionForm.constants';
import { rejectFormStyles } from './RejectConditionForm.styles';
import { RejectConditionFormTestIds } from './RejectConditionForm.testIds';

const RejectConditionForm = ({ condition, isBrokerCompany, loanId }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const [reason, setReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const trimmedReason = reason.trim();
  const isSubmitDisabled = !trimmedReason || isSaving;

  const onReasonChange = useCallback(event => {
    setReason(event.target.value);
  }, []);

  const refreshConditions = useCallback(() => {
    const fetchConditions = isBrokerCompany
      ? getLoanBrokerConditionByApplicationIdAction({ applicationId })
      : getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true });

    return Promise.all([dispatch(fetchConditions), dispatch(getLoanApplicationTasksAction())]);
  }, [applicationId, dispatch, isBrokerCompany, loanId]);

  const onCancel = useCallback(() => {
    dispatch(closeDialogAction());
  }, [dispatch]);

  const onSubmit = useCallback(() => {
    setIsSaving(true);

    dispatch(rejectLoanConditionAction({ loanConditionId: condition.id, reason: trimmedReason }))
      .then(refreshConditions)
      .then(() =>
        dispatch(
          showMessageAction({
            message: `Condition #${condition.number} rejected.`,
            variant: 'success',
          }),
        ),
      )
      .then(() => dispatch(closeDialogAction()))
      .finally(() => setIsSaving(false));
  }, [condition.id, condition.number, dispatch, refreshConditions, trimmedReason]);

  return (
    <Box sx={rejectFormStyles}>
      <TextFieldCDS
        autoFocus
        fullWidth
        multiline
        required
        dataTestId={RejectConditionFormTestIds.REASON_INPUT}
        disabled={isSaving}
        label="Rejection Reason"
        maxLength={REJECTION_REASON_MAX_LENGTH}
        name="reason"
        rows={5}
        value={reason}
        onChange={onReasonChange}
      />
      <ButtonGroupWrapper>
        <Button
          data-testid={RejectConditionFormTestIds.CANCEL_BUTTON}
          disabled={isSaving}
          variant="outlined"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          data-testid={RejectConditionFormTestIds.REJECT_BUTTON}
          disabled={isSubmitDisabled}
          onClick={onSubmit}
        >
          Reject
        </Button>
      </ButtonGroupWrapper>
    </Box>
  );
};

RejectConditionForm.propTypes = {
  condition: PropTypes.object.isRequired,
  isBrokerCompany: PropTypes.bool.isRequired,
  loanId: PropTypes.string.isRequired,
};

export default memo(RejectConditionForm);
